import ReactPlayer from "react-player/lazy";
import CircularProgress from "@mui/material/CircularProgress"
import React, { memo, useEffect, useRef, useState } from "react";
import videojs, { VideoJsPlayer, VideoJsPlayerOptions } from "video.js";

import Helpers from "../../commons/helpers";
import AssetService from "../../services/asset/asset.service";

import { IProgress } from "../../commons/interfaces";
import { Constants } from "../../constants/constants";
import "./styles.css";


const EVENTS = {
    LOAD_START: "loadstart",
    PROGRESS: "progress",
    SUSPEND: "suspend",
    ABORT: "abort",
    ERROR: "error",
    EMPTIED: "emptied",
    STALLED: "stalled",
    LOADED_METADATA: "loadedmetadata",
    LOADED_DATA: "loadeddata",
    CAN_PLAY: "canplay",
    CAN_PLAY_THROUGH: "canplaythrough",
    PLAYING: "playing",
    WAITING: "waiting",
    SEEKING: "seeking",
    SEEKED: "seeked",
    ENDED: "ended",
    DURATION_CHANGE: "durationchange",
    TIME_UPDATE: "timeupdate",
    PLAY: "play",
    PAUSE: "pause",
    RATE_CHANGE: "ratechange",
    RESIZE: "resize",
    VOLUME_CHANGE: "volumechange",
    CONTEXT_MENU: "contextmenu",
}

interface IProps {
    fileId?: string;
    autoPlay?: boolean;
    controls?: boolean;
    seekToOnReady?: number; // In second
    onReady?: (player: VideoJsPlayer) => void;
    onProgress?: (progress: IProgress) => void;
}

const VideoPlayer: React.FC<IProps> = (props: IProps) => {
    const videoElementRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<VideoJsPlayer | null>(null);

    const youtubePlayerRef = useRef<ReactPlayer>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [youtubeUrl, setYoutubeUrl] = useState<string>();
    const [youtubeDuration, setYoutubDuration] = useState<number>(0);

    const [options, setOptions] = useState<VideoJsPlayerOptions>({
        autoplay: false,
        controls: true,
        aspectRatio: "16:9",
        responsive: true,
        fluid: true,
        html5: {
            vhs: { cacheEncryptionKeys: true }
        },
        sources: [{
            src: "",
            type: ""
        }]
    });

    const getVideoUrl = async (fileId: string): Promise<string> => {
        try {
            setIsLoading(true);

            const youtubeVideo = await AssetService.getExternalUrlIds([fileId]);
            if (youtubeVideo.length > 0 && Helpers.isYoutubeUrl(youtubeVideo[0].url)) {
                setYoutubeUrl(youtubeVideo[0].url);
                return youtubeVideo[0].url;
            }

            const videoInfo = await AssetService.getAssetInfo(fileId);
            if (videoInfo.accessUrl?.includes(".m3u8")) {
                return await AssetService.getM3u8Url(fileId);
            }

            const newFileId = Helpers.getFileAccessUrl(fileId);

            return newFileId;
        } catch (error) {
            console.log(error);
            return "";
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        return () => {
            if (playerRef.current) {
                playerRef.current.dispose();
                playerRef.current = null;
                console.log("Player is disposed");
            }
        };
    }, [playerRef])

    useEffect(() => {
        (async () => {
            if (Helpers.isNullOrEmpty(props.fileId)) {
                return;
            }

            if (!Helpers.isNullOrEmpty(playerRef.current)) {
                return;
            }

            let autoplay = options.autoplay;
            if (!Helpers.isNullOrEmpty(props.autoPlay)) {
                autoplay = props.autoPlay;
            }

            let controls = options.controls;
            if (!Helpers.isNullOrEmpty(props.controls)) {
                controls = props.controls;
            }

            let src = options.src || "";
            let type = options.sources && options.sources[0].type;
            if (!Helpers.isNullOrEmpty(props.fileId)) {
                src = await getVideoUrl(props.fileId);
                switch (Helpers.getFileExtesion(src)) {
                    case ".m3u8":
                        type = "application/x-mpegURL";
                        break;

                    case ".webm":
                        type = "video/webm";
                        break;

                    case ".ogv":
                        type = "video/ogv";
                        break;

                    default:
                        type = "video/mp4";
                        break;
                }
            }

            setOptions((oldOptions: any) => ({
                ...oldOptions,
                autoplay,
                controls,
                sources: [{
                    src,
                    type
                }]
            }))
        })()
    }, [props.autoPlay, props.controls, props.fileId])

    useEffect(() => {
        const player = playerRef.current;

        if (!player) {
            const videoElement = videoElementRef.current;
            if (!videoElement) {
                return;
            }

            if (
                !Helpers.isNullOrEmpty(options.sources) &&
                !Helpers.isNullOrEmpty(options.sources[0].src) &&
                !Helpers.isNullOrEmpty(options.sources[0].type)
            ) {
                const player = playerRef.current = videojs(videoElement, options, () => {
                    console.log("Player is ready");
                    props.onReady && props.onReady(player);

                    // Seek to specific second
                    props.seekToOnReady && player.currentTime(props.seekToOnReady);

                    player.on(EVENTS.CONTEXT_MENU, (e: any) => {
                        e.preventDefault();
                    });

                    player.on(EVENTS.TIME_UPDATE, () => {
                        props.onProgress && props.onProgress({
                            currentTime: player.currentTime(),
                            duration: player.duration(),
                        })
                    });
                });
            }
        }

        if (player) {
            if (options.controls) {
                player.controls(options.controls)
            }
            if (options.autoplay) {
                player.autoplay(options.autoplay);
            }
            if (options.sources) {
                player.src(options.sources);
            }
        }
    }, [options]);

    const handleYoutubeOnStart = () => {
        if (youtubePlayerRef.current) {
            setYoutubDuration(youtubePlayerRef.current.getDuration())
            props.seekToOnReady && youtubePlayerRef.current.seekTo(props.seekToOnReady, "seconds");
        }
    }

    const handleYoutubeOnProgress = (progress: {
        played: number;
        playedSeconds: number;
        loaded: number;
        loadedSeconds: number;
    }) => {
        props.onProgress && props.onProgress({
            currentTime: progress.playedSeconds,
            duration: youtubeDuration,
        })
    }

    if (isLoading) {
        return (
            <div className="w-100 d-flex justify-content-center align-items-center">
                <CircularProgress />
            </div>
        )
    }

    return (
        Helpers.isNullOrEmpty(youtubeUrl) ? (
            <div data-vjs-player>
                <video
                    ref={videoElementRef}
                    className="video-js vjs-big-play-centered"
                />
            </div>
        ) : (
            <div className="player-wrapper">
                <ReactPlayer
                    ref={youtubePlayerRef}
                    className="react-player"
                    controls
                    width="100%"
                    height="100%"
                    url={youtubeUrl}
                    onStart={handleYoutubeOnStart}
                    onProgress={handleYoutubeOnProgress}
                />
            </div>
        )
    );
}

export default memo(VideoPlayer);